import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Device } from '@twilio/voice-sdk';
import { get_api } from '../../../redux/api_funcs';
import { get_api_url } from '../../../utils/urls';
import { CallbackHandler } from '../../../myt_chat/chat/callbacks/handler';
import MytNotification from '../../../myt_chat/notification';
import IncomingCallPill from './IncomingCallPill';

const getUrl = (endpoint) => {
  if (import.meta.env.VITE_TWILIO_CALL_BASE_URL) {
    return `${import.meta.env.VITE_TWILIO_CALL_BASE_URL}${endpoint}`;
  }
  return get_api_url(endpoint);
};

// Server issues Voice tokens with a 1 h TTL (GenVoiceToken). Refresh well
// before expiry so `<Client>` routing never lapses.
const TOKEN_REFRESH_MS = 50 * 60 * 1000;

// Mount this once at the app shell (e.g. inside Home). It keeps a Twilio
// Voice Device registered for as long as the agent is marked available,
// regardless of which CMS page they're on, so incoming <Client> TwiML can
// always reach the browser. Renders the IncomingCallPill globally.
export default function GlobalIncomingCallManager() {
  const [available, setAvailable] = useState(false);
  // 'idle' (not available) | 'registering' | 'registered' | 'error'
  const [deviceState, setDeviceState] = useState('idle');
  const [deviceError, setDeviceError] = useState(null);
  const [deviceIdentity, setDeviceIdentity] = useState(null);

  const [pendingByCallSid, setPendingByCallSid] = useState({});
  const [activeIncoming, setActiveIncoming] = useState(null);
  const [incomingStatus, setIncomingStatus] = useState(null);
  const [incomingMuted, setIncomingMuted] = useState(false);

  const deviceRef = useRef(null);
  const activeCallRef = useRef(null);
  // Held in a ref so the Device-lifecycle useEffect doesn't re-fire (and
  // teardown the Device!) every time pendingByCallSid mutates.
  const attachIncomingCallRef = useRef(null);

  const stopRing = useCallback((callSid) => {
    if (!callSid) return;
    MytNotification.stopIncomingCallRing(`incoming-call-${callSid}`);
  }, []);

  // The ring is started in home/index with the parent (PSTN) CallSid from the
  // relay, but the SDK exposes both the parent (via customParameters) and the
  // child (call.parameters.CallSid) legs — and either can be missing in some
  // edge cases. Stop every id we know of so we never leave a ring playing.
  const stopAllRings = useCallback(
    (sids) => {
      if (!sids) return;
      const seen = new Set();
      for (const s of sids) {
        if (!s || seen.has(s)) continue;
        seen.add(s);
        stopRing(s);
      }
    },
    [stopRing]
  );

  const broadcastOngoingRefresh = useCallback(() => {
    window.dispatchEvent(new CustomEvent('twilio-ongoing-calls-refresh'));
  }, []);

  const attachIncomingCall = useCallback(
    (call) => {
      activeCallRef.current = call;

      // The SDK's call.parameters.CallSid is the child (Client) leg SID,
      // but the ring was started with the parent (PSTN) SID from the relay.
      // We track BOTH so stopRing hits whichever the ring was keyed on —
      // ParentCallSid is the expected id, but it can be missing if the TwiML
      // <Parameter> didn't propagate, in which case the ring was keyed on
      // whatever call_sid the relay carried (which equals the child here).
      const parentSid = call?.customParameters?.get?.('ParentCallSid');
      const childSid =
        call?.parameters?.CallSid || call?.customParameters?.get?.('CallSid') || null;
      const ringSids = [parentSid, childSid].filter(Boolean);
      const sid = parentSid || childSid;
      const meta = ringSids.map((s) => pendingByCallSid[s]).find(Boolean) || null;

      setActiveIncoming({
        callSid: sid || null,
        ringSids,
        fromNumber: (meta && meta.from_number) || call?.parameters?.From || call?.from || 'Unknown',
        studentName: meta && meta.student_name ? meta.student_name : null
      });
      setIncomingStatus('ringing');
      setIncomingMuted(false);

      call.on('accept', () => {
        setIncomingStatus('connected');
        stopAllRings(ringSids);
        broadcastOngoingRefresh();
      });
      call.on('disconnect', () => {
        setIncomingStatus('ended');
        stopAllRings(ringSids);
        broadcastOngoingRefresh();
      });
      call.on('cancel', () => {
        setIncomingStatus('ended');
        stopAllRings(ringSids);
        broadcastOngoingRefresh();
      });
      call.on('reject', () => {
        setIncomingStatus('rejected');
        stopAllRings(ringSids);
        broadcastOngoingRefresh();
      });
      call.on('error', (err) => {
        console.error('global_incoming.error', err);
        setIncomingStatus('failed');
        stopAllRings(ringSids);
        broadcastOngoingRefresh();
      });
      call.on('mute', (isMuted) => setIncomingMuted(!!isMuted));
    },
    [pendingByCallSid, stopAllRings, broadcastOngoingRefresh]
  );

  useEffect(() => {
    attachIncomingCallRef.current = attachIncomingCall;
  }, [attachIncomingCall]);

  // Fetch availability once on mount, then rely on the
  // `twilio-agent-availability-changed` window event. Local toggles dispatch
  // it directly; server-side changes arrive via a chat relay that home/index
  // translates into the same event, so every tab stays in sync without polling.
  useEffect(() => {
    let cancelled = false;

    const refresh = async () => {
      try {
        const resp = await get_api(getUrl('/v2/twilio_call/availability'), true);
        if (cancelled) return;
        setAvailable(!!resp.data?.data?.available);
      } catch (err) {
        console.error('global_incoming.availability_fetch_failed', err);
      }
    };

    refresh();

    const onAvailabilityChanged = (e) => {
      const next = !!e?.detail?.available;
      setAvailable(next);
    };
    window.addEventListener('twilio-agent-availability-changed', onAvailabilityChanged);

    return () => {
      cancelled = true;
      window.removeEventListener('twilio-agent-availability-changed', onAvailabilityChanged);
    };
  }, []);

  // Twilio Device lifecycle follows `available`.
  useEffect(() => {
    let cancelled = false;
    let refreshTimer = null;

    const fetchToken = async () => {
      const resp = await get_api(getUrl('/v2/twilio_call/token'), true);
      return {
        token: resp.data?.data?.token,
        identity: resp.data?.data?.identity
      };
    };

    const scheduleRefresh = () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(async () => {
        refreshTimer = null;
        if (cancelled) return;
        const device = deviceRef.current;
        if (!device) return;
        try {
          const { token } = await fetchToken();
          if (cancelled || !token || !deviceRef.current) return;
          deviceRef.current.updateToken(token);
          // eslint-disable-next-line no-console
          console.info('[twilio] Device token refreshed');
          scheduleRefresh();
        } catch (err) {
          console.error('global_incoming.device.token_refresh_failed', err);
          // Retry in 1 min; next register effect will also recover.
          refreshTimer = setTimeout(scheduleRefresh, 60 * 1000);
        }
      }, TOKEN_REFRESH_MS);
    };

    const teardown = () => {
      console.log('[twilio] Tearing down Device');

      if (refreshTimer) {
        clearTimeout(refreshTimer);
        refreshTimer = null;
      }
      const device = deviceRef.current;
      deviceRef.current = null;
      if (!device) return;
      try {
        device.removeAllListeners?.();
        device.destroy();
      } catch (err) {
        console.error('global_incoming.device.destroy_failed', err);
      }
    };

    if (!available) {
      setDeviceState('idle');
      setDeviceError(null);
      teardown();
      return teardown;
    }

    setDeviceState('registering');
    setDeviceError(null);

    (async () => {
      try {
        const { token, identity } = await fetchToken();
        if (cancelled || !token) {
          if (!cancelled) {
            setDeviceState('error');
            setDeviceError('no token');
          }
          return;
        }

        // eslint-disable-next-line no-console
        console.info('[twilio] Registering Device with identity =', identity);
        if (!cancelled) setDeviceIdentity(identity || null);

        if (deviceRef.current) teardown();

        const device = new Device(token);
        deviceRef.current = device;

        device.on('incoming', (call) => {
          attachIncomingCallRef.current?.(call);
        });
        device.on('error', (err) => {
          console.error('global_incoming.device.error', err);
          if (!cancelled) {
            setDeviceState('error');
            setDeviceError(err?.message || String(err));
          }
        });
        device.on('registered', () => {
          // eslint-disable-next-line no-console
          console.info(
            '[twilio] Device registered for incoming calls — identity =',
            device.identity || identity
          );
          if (!cancelled) setDeviceState('registered');
        });
        device.on('unregistered', () => {
          // eslint-disable-next-line no-console
          console.info('[twilio] Device unregistered');
        });
        device.on('tokenWillExpire', async () => {
          console.log('[twilio] Device token will expire soon, refreshing...');
          // Belt-and-braces: Twilio SDK also warns ~5 min before expiry.
          try {
            const refreshed = await fetchToken();
            if (cancelled || !refreshed.token || !deviceRef.current) return;
            deviceRef.current.updateToken(refreshed.token);
            // eslint-disable-next-line no-console
            console.info('[twilio] Device token refreshed via tokenWillExpire');
          } catch (err) {
            console.error('global_incoming.device.tokenWillExpire_refresh_failed', err);
          }
        });

        await device.register();
        console.log('[twilio] Device register() called');
        scheduleRefresh();
      } catch (err) {
        console.error('global_incoming.device.register_failed', err);
        if (!cancelled) {
          setDeviceState('error');
          setDeviceError(err?.message || String(err));
        }
      }
    })();

    return () => {
      cancelled = true;
      teardown();
    };
  }, [available]);

  // Track relay metadata so the pill can show name, and so we can reconcile
  // with the SDK's incoming event by CallSid.
  useEffect(() => {
    const handler = (item) => {
      if (!item || !item.data) return;
      const { type, call_sid: callSid } = item.data;
      if (!callSid) return;

      if (type === 'incoming_call') {
        setPendingByCallSid((prev) => ({ ...prev, [callSid]: item.data }));
        broadcastOngoingRefresh();
        return;
      }

      if (type === 'incoming_call_ended') {
        broadcastOngoingRefresh();
      }
    };
    CallbackHandler.shared.registerRelayItemListener(handler);
    return () => {
      CallbackHandler.shared.unregisterRelayItemListener(handler);
    };
  }, [broadcastOngoingRefresh]);

  const acceptIncoming = () => {
    setIncomingStatus('connecting');
    try {
      activeCallRef.current?.accept();
    } catch (err) {
      console.error('global_incoming.accept_failed', err);
      setIncomingStatus('failed');
      // accept() threw — the SDK will not emit 'accept'/'disconnect', so the
      // ring would otherwise play until MytNotification's max-timeout.
      stopAllRings(activeIncoming?.ringSids);
    }
  };

  const rejectIncoming = () => {
    try {
      activeCallRef.current?.reject();
    } catch (err) {
      console.error('global_incoming.reject_failed', err);
    }
    stopAllRings(activeIncoming?.ringSids);
    setIncomingStatus('rejected');
  };

  const toggleIncomingMute = () => {
    const call = activeCallRef.current;
    if (!call) return;
    try {
      const next = !call.isMuted();
      call.mute(next);
      setIncomingMuted(next);
    } catch (err) {
      console.error('global_incoming.mute_failed', err);
    }
  };

  const endIncoming = () => {
    try {
      activeCallRef.current?.disconnect();
    } catch (err) {
      console.error('global_incoming.disconnect_failed', err);
    }
  };

  // Terminal pill states auto-close after a short delay so the agent isn't
  // forced to click the X manually. Manual close (onClose) short-circuits.
  useEffect(() => {
    if (!incomingStatus) return undefined;
    if (!['ended', 'rejected', 'failed', 'disconnected'].includes(incomingStatus)) {
      return undefined;
    }
    const timer = setTimeout(() => {
      closeIncomingPill();
    }, 5000);
    return () => clearTimeout(timer);
    // closeIncomingPill is defined below and doesn't change between renders;
    // including it would recreate the timer every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingStatus]);

  const closeIncomingPill = () => {
    stopAllRings(activeIncoming?.ringSids);
    setActiveIncoming(null);
    setIncomingStatus(null);
    activeCallRef.current = null;
    const ringSids = activeIncoming?.ringSids;
    if (ringSids && ringSids.length > 0) {
      setPendingByCallSid((prev) => {
        const next = { ...prev };
        for (const s of ringSids) delete next[s];
        return next;
      });
    }
  };

  const badgeColor =
    deviceState === 'registered'
      ? { bg: '#dcfce7', fg: '#166534', dot: '#16a34a' }
      : deviceState === 'registering'
        ? { bg: '#fef3c7', fg: '#92400e', dot: '#f59e0b' }
        : deviceState === 'error'
          ? { bg: '#fee2e2', fg: '#991b1b', dot: '#dc2626' }
          : null; // 'idle' → hidden

  return (
    <>
      {/* {badgeColor && (
        <div
          title={
            deviceError ? `Twilio Device error: ${deviceError}` : `Twilio Device: ${deviceState}`
          }
          style={{
            position: 'fixed',
            bottom: 12,
            right: 12,
            zIndex: 9998,
            padding: '4px 10px',
            borderRadius: 9999,
            background: badgeColor.bg,
            color: badgeColor.fg,
            fontSize: 11,
            fontWeight: 500,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0,0,0,0.04)'
          }}
        >
          <span
            style={{
              display: 'inline-block',
              width: 6,
              height: 6,
              borderRadius: 9999,
              background: badgeColor.dot
            }}
          />
          Twilio: {deviceState}
          {deviceIdentity && deviceState === 'registered' && (
            <span style={{ color: '#4b5563', fontWeight: 400 }}> · {deviceIdentity}</span>
          )}
        </div>
      )} */}

      {activeIncoming && incomingStatus ? (
        <IncomingCallPill
          fromNumber={activeIncoming.fromNumber}
          studentName={activeIncoming.studentName}
          status={incomingStatus}
          muted={incomingMuted}
          onAccept={acceptIncoming}
          onReject={rejectIncoming}
          onToggleMute={toggleIncomingMute}
          onEndCall={endIncoming}
          onClose={closeIncomingPill}
        />
      ) : null}
    </>
  );
}
